import { Select, TimePicker } from "antd";
import dayjs from "dayjs";
import { Field, Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import ButtonLoader from "@/Components/ButtonLoader";
import FieldErrorMessage from "@/Components/common/FieldErrorMessage";
import { RequiredSymbol } from "@/Components/common/RequiredSymbol";
import { TimePickerClock } from "@/Components/Icons/TimePickerClock";
import {
  getDayWiseRates,
  updateActiveStep,
  updateJobData,
  updateJobShiftObjFromLocal,
  updateStepsValidated,
} from "@/reducers/clientcreatejob";
import { createJobValidations } from "@/utilities/CommonText";
import {
  showErrorToast,
  showWarningToast,
  handleKeyUpForTimePicker,
} from "@/utilities/utils";

import DayRateIcon from "../../../../Icons/DayRateIcon";
import { SelectDownIcon } from "../../../../Icons/SelectDownIcon";
import ResetButton from "../buttons/ResetButton";
import { JobShiftLists } from "../JobShiftLists";
import { JobSteps } from "../JobSteps";
import { Summary } from "../Summary";
import CustomField from "../timeselection/CustomField";

/**
 * TimeSelection component for specifying working hours.
 *
 * @param {Function} updateDays - a function to update the days
 * @return {JSX.Element} the TimeSelection component
 */
export const TimeSelection = ({ updateDays }) => {
  const startTimeRef = useRef(null);
  const endTimeRef = useRef(null);
  const {
    jobData,
    timeSelectionsPresets,
    getDayWiseApiLoading,
    isUnitsValid,
    jobShiftObj,
  } = useSelector((state) => state.clientCreateJob);
  const dispatch = useDispatch();
  const initialUpdate = useRef(true);
  const [breakTimeLabelClicked, setBreakTimeLabelClicked] = useState(false);
  const [adminTimeLabelClicked, setAdminTimeLabelClicked] = useState(false);
  useEffect(() => {
    if (initialUpdate.current) {
      initialUpdate.current = false;
      return;
    }
    if (jobShiftObj.length === 0) {
      updateDays();
    }
    const rateArray = Array.isArray(jobData?.rateCardList)
      ? jobData?.rateCardList
      : [jobData?.rateCardList];

    if (
      jobData.startDate &&
      jobData.endDate &&
      jobData.startTime &&
      jobData.endTime &&
      rateArray.length > 0
    ) {
      let dayObj = {};
      jobData.selectDateJobDates.forEach((date) => {
        let day = dayjs(date, "DD-MM-YYYY").format("dddd");
        if (dayObj[day]) {
          dayObj[day].push(date);
        } else {
          dayObj[day] = [date];
        }
      });
      Object.keys(dayObj).forEach((day) => {
        dispatch(
          getDayWiseRates({
            day,
            selected_date: dayObj[day],
          }),
        );
      });
    }
  }, [
    jobData.startTime,
    jobData.endTime,
    jobData.rateCardList,
    jobData.noOfVisit,
  ]);

  const initialUpdateInLocal = useRef(true);
  useEffect(() => {
    if (initialUpdateInLocal.current) {
      initialUpdateInLocal.current = false;
      return;
    }
    dispatch(
      updateJobShiftObjFromLocal({
        fromTimeSelection: true,
      }),
    );
  }, [
    jobData.startTime,
    jobData.endTime,
    jobData.breakTime,
    jobData.adminTime,
  ]);
  useEffect(() => {
    if (startTimeRef.current) {
      startTimeRef.current
        .querySelector(".ant-picker-input input")
        .addEventListener("keyup", (e) => {
          handleKeyUpForTimePicker(e);
        });
    }
    if (endTimeRef.current) {
      endTimeRef.current
        .querySelector(".ant-picker-input input")
        .addEventListener("keyup", (e) => {
          handleKeyUpForTimePicker(e);
        });
    }
  }, []);

  const rateArray = Array.isArray(jobData?.rateObj)
    ? jobData?.rateObj
    : [jobData?.rateObj];
  const isFirstDivVisible = jobData?.rateObj?.length > 1;

  return (
    <div className="TimeSelection_Wrapper">
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-[1fr,280px] xl:grid-cols-[1fr,330px]">
          <div className="grid h-fit gap-4">
            <JobSteps />
            <div className="rounded-lg bg-white p-6">
              <div className="mb-2 flex justify-between">
                <h4 className="text-[18px] font-medium leading-[normal] text-black">
                  Specify working Hours
                </h4>
                <div className="flex gap-4">
                  <ResetButton />
                </div>
              </div>
              <Formik
                initialValues={{
                  rateCardList: jobData.rateCardList || [],
                  startTime: jobData.startTime || "",
                  endTime: jobData.endTime || "",
                  breakTime: jobData.breakTime || "",
                  adminTime: jobData.adminTime || "",
                  noOfVisit: jobData.noOfVisit || "",
                }}
                enableReinitialize={true}
                onSubmit={(values, { setSubmitting }) => {
                  // let hasEmptyRateValue = jobShiftObj.some((item) => {
                  //   return item.rate_values.length === 0;
                  // });
                  // if (hasEmptyRateValue) {
                  //   showErrorToast(
                  //     "Please select date and time properly!",
                  //   );
                  //   setSubmitting(false);
                  //   return;
                  // }
                  // if (isUnitsValid) {
                  //   showErrorToast("Shift can't be of less than 30 minutes");
                  //   setSubmitting(false);
                  //   return;
                  // }
                  if (getDayWiseApiLoading) {
                    showWarningToast(createJobValidations.isFetchingRates, {
                      id: "isFetchingRates",
                    });
                    setSubmitting(false);
                    return;
                  }
                  initialUpdate.current = true;
                  initialUpdateInLocal.current = true;
                  dispatch(updateActiveStep(4));
                  dispatch(
                    updateStepsValidated({
                      timeSelection: true,
                      verification: true,
                    }),
                  );
                }}
                validate={(values) => {
                  const errors = {};
                  if (!values.startTime) {
                    errors.startTime = "Shift Start Time is Required";
                  }
                  if (!values.endTime) {
                    errors.endTime = "Shift End Time is Required";
                  }
                  if (values.rateCardList.length === 0) {
                    errors.rateCardList = "Select One Rate Structure";
                  }
                  return errors;
                }}
              >
                {({
                  submitForm,
                  isSubmitting,
                  errors,
                  touched,
                  isValid,
                  values,
                  setFieldValue,
                }) => (
                  <Form>
                    <div className="grid grid-cols-1 gap-4">
                      <div
                        className={`grid gap-4 ${
                          isFirstDivVisible
                            ? "grid-cols-[minmax(0,380px),minmax(0,168px),minmax(0,168px),minmax(0,168px)]"
                            : "grid-cols-[minmax(0,180px),minmax(0,168px),minmax(0,168px),minmax(0,168px)]"
                        }`}
                      >
                        {isFirstDivVisible ? (
                          <div className="rateCardList_div flex flex-col gap-2">
                            <label className="label" htmlFor="rateCardList">
                              Rate Structure
                              <RequiredSymbol />
                            </label>
                            <div className="relative [&_.ant-select-selection-item>div>span]:truncate">
                              <Select
                                showSearch
                                style={{ width: "100%" }}
                                placeholder="Select Rate Structure"
                                optionFilterProp="children"
                                className="ease min-h-[40px] w-full overflow-hidden rounded-lg border border-lightGrey text-[14px] font-medium leading-[normal] text-black transition-all duration-200 focus:border-primary"
                                suffixIcon={
                                  <SelectDownIcon cls={"text-txtColor"} />
                                }
                                onChange={(e) => {
                                  dispatch(updateJobData({ rateCardList: e }));
                                }}
                                value={values.rateCardList}
                              >
                                {jobData?.rateObj?.length &&
                                  jobData?.rateObj.map((item, index) => {
                                    const {
                                      id,
                                      ref_no = "Ref no not found",
                                      description = "Des",
                                    } = item?.data;
                                    return (
                                      <Select.Option
                                        value={id}
                                        key={index}
                                        className="text-[14px] font-medium text-grey"
                                      >
                                        <div className="grid grid-cols-[1fr_auto] items-center gap-1 whitespace-break-spaces">
                                          <span className="">
                                            <b>{ref_no} -</b> {description}
                                          </span>
                                          {item?.is_day_rate == 1 ? (
                                            <span className="flex !h-[26px] !w-[101.5px] !items-center gap-[6px] rounded-lg bg-lightblueBG px-2 py-[1px] text-[14px] text-primary">
                                              <DayRateIcon cls="w-[18px] h-[18px]" />
                                              Day rate
                                            </span>
                                          ) : null}
                                        </div>
                                      </Select.Option>
                                    );
                                  })}
                              </Select>
                            </div>
                            <FieldErrorMessage
                              name="rateCardList"
                              className={"text-red"}
                            />
                          </div>
                        ) : null}

                        <div className="flex flex-col gap-2">
                          <label className="label" htmlFor="startTime">
                            Shift Start Time
                            <RequiredSymbol />
                          </label>
                          <div
                            className="custom_time_picker_wrapper relative"
                            ref={startTimeRef}
                          >
                            <TimePicker
                              // inputReadOnly
                              format="HH:mm"
                              showNow={false}
                              // changeOnBlur={true}
                              needConfirm={false}
                              value={
                                values.startTime
                                  ? dayjs(values.startTime, "HH:mm")
                                  : null
                              }
                              suffixIcon={
                                <TimePickerClock cls={"text-primary"} />
                              }
                              minuteStep={15}
                              placeholder="00:00"
                              onChange={(value) => {
                                let startTime = value.format("HH:mm");
                                setFieldValue("startTime", startTime);
                                let endTime = jobData.endTime;
                                // if endTime is greater than startTime then remove it
                                if (endTime) {
                                  let startTimeObj = dayjs(
                                    startTime,
                                    "HH:mm",
                                  ).add(30, "minute");
                                  let endTimeObj = dayjs(
                                    endTime,
                                    "HH:mm",
                                  ).subtract(30, "minute");
                                  if (startTimeObj.isAfter(endTimeObj)) {
                                    endTime = null;
                                    dispatch(updateJobData({ endTime }));
                                  }
                                }
                                dispatch(updateJobData({ startTime }));
                              }}
                              allowClear={false}
                              popupClassName="custom_time_picker"
                            />
                            <FieldErrorMessage
                              name="startTime"
                              className={"text-red"}
                            />
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="label" htmlFor="endTime">
                            Shift End Time
                            <RequiredSymbol />
                          </label>
                          <div
                            className="custom_time_picker_wrapper relative"
                            ref={endTimeRef}
                          >
                            <TimePicker
                              // use12Hours
                              // inputReadOnly
                              showNow={false}
                              // changeOnBlur={true}
                              needConfirm={false}
                              format="HH:mm"
                              value={
                                values.endTime
                                  ? dayjs(values.endTime, "HH:mm")
                                  : null
                              }
                              suffixIcon={
                                <TimePickerClock cls={"text-primary"} />
                              }
                              minuteStep={15}
                              // disabledTime={() => {
                              // let startTimeObj = dayjs(
                              //   jobData.startTime,
                              //   "HH:mm",
                              // ).add(30, "minute");
                              // const startHour = startTimeObj.hour();
                              // const startMinute = startTimeObj.minute();
                              /**
                               * This function calculates the disabled hours.
                               *
                               * @return {array} The array of disabled hours
                               */
                              // const disabledHours = () => {
                              //   let hours = [];
                              //   for (let i = 0; i < startHour; i++) {
                              //     hours.push(i);
                              //   }
                              //   return hours;
                              // };
                              /**
                               * A function to generate an array of disabled minutes based on the selected hour.
                               *
                               * @param {number} selectedHour - The selected hour for which disabled minutes are to be generated.
                               * @return {Array} An array of disabled minutes.
                               */
                              // const disabledMinutes = (selectedHour) => {
                              //   if (selectedHour === startHour) {
                              //     let minutes = [];
                              //     for (let i = 0; i < startMinute; i++) {
                              //       minutes.push(i);
                              //     }
                              //     return minutes;
                              //   }
                              // };
                              // return {
                              //   disabledHours: disabledHours,
                              //   disabledMinutes: disabledMinutes,
                              // };
                              // }}
                              placeholder="00:00"
                              onChange={(value) => {
                                // let startTime = jobData.startTime;
                                // if(startTime) {
                                //   let startTimeObj = dayjs(
                                //     jobData.startTime,
                                //     "HH:mm",
                                //   ).add(30, "minute");
                                //   let endTimeObj = value;
                                //   if (endTimeObj.isBefore(startTimeObj)) {
                                //     showErrorToast("Shift End Time can't be less than Start Time");
                                //     dispatch(updateJobData({ endTime: jobData.endTime }))
                                //     return;
                                //   }
                                // }
                                let endTime = value.format("HH:mm");
                                setFieldValue("endTime", endTime);
                                dispatch(updateJobData({ endTime }));
                              }}
                              allowClear={false}
                              popupClassName="custom_time_picker"
                            />
                            <FieldErrorMessage
                              name="endTime"
                              className={"text-red"}
                            />
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="label" htmlFor="noOfVisit">
                            No. Of Visit
                          </label>
                          <Field
                            id="noOfVisit"
                            type="text"
                            name="noOfVisit"
                            placeholder="0"
                            className="ease h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black transition-all duration-200 placeholder:font-medium placeholder:text-txtColor focus:border-primary"
                            onKeyPress={(e) => {
                              if (!/[0-9]/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onChange={(e) => {
                              dispatch(
                                updateJobData({
                                  noOfVisit: e.target.value,
                                  totalClientChargesPerVisit: 0,
                                }),
                              );
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex select-none flex-col items-start gap-2">
                        <label className="label" htmlFor="breakTime">
                          Un-Paid Break
                        </label>
                        <div className="flex gap-3">
                          {timeSelectionsPresets.map((item, index) => (
                            <div
                              className={`pointer-events-auto flex h-[35px] w-[95px] cursor-pointer items-center justify-center rounded-lg text-sm font-medium ${
                                jobData.breakTime == item.value &&
                                !jobData.isBreakTimeCustom
                                  ? "bg-primary text-white"
                                  : "bg-lightblue text-primary"
                              }`}
                              key={index}
                              onClick={() => {
                                if (getDayWiseApiLoading) {
                                  showWarningToast(
                                    createJobValidations.isFetchingRates,
                                    {
                                      id: "isFetchingRates",
                                    },
                                  );
                                  return;
                                }
                                if (jobData.breakTime == item.value) {
                                  dispatch(
                                    updateJobData({
                                      breakTime: "00:00",
                                      isBreakTimeCustom: false,
                                    }),
                                  );
                                } else {
                                  let startEndDiff = dayjs(
                                    jobData.endTime,
                                    "HH:mm",
                                  ).diff(
                                    dayjs(jobData.startTime, "HH:mm"),
                                    "minute",
                                  );
                                  let breakTimeDiff = dayjs(item.value, "HH:mm")
                                    .add(30, "minute")
                                    .diff(dayjs("00:00", "HH:mm"), "minute");
                                  if (breakTimeDiff > startEndDiff) {
                                    showErrorToast(
                                      createJobValidations.maxBreakTime,
                                      {
                                        id: "maxBreakTime",
                                      },
                                    );
                                    return;
                                  } else {
                                    dispatch(
                                      updateJobData({
                                        breakTime: item.value,
                                        isBreakTimeCustom: false,
                                      }),
                                    );
                                  }
                                  setBreakTimeLabelClicked(
                                    !breakTimeLabelClicked,
                                  );
                                }
                              }}
                            >
                              {item.label}
                            </div>
                          ))}
                          <CustomField
                            isCustom={jobData.isBreakTimeCustom}
                            handleBlur={(val, customVal = true) => {
                              if (val !== jobData.breakTime) {
                                dispatch(
                                  updateJobData({
                                    breakTime: val,
                                    isBreakTimeCustom: customVal,
                                  }),
                                );
                              }
                            }}
                            curTime={jobData.breakTime}
                            labelClicked={breakTimeLabelClicked}
                          />
                        </div>
                      </div>
                      <div className="flex select-none flex-col items-start gap-2">
                        <label className="label" htmlFor="adminTime">
                          Admin Time
                        </label>
                        <div className="flex gap-3">
                          {timeSelectionsPresets.map((item, index) => (
                            <div
                              key={index}
                              className={`pointer-events-auto flex h-[35px] w-[95px] cursor-pointer items-center justify-center rounded-lg text-sm font-medium ${
                                jobData.adminTime == item.value &&
                                !jobData.isAdminTimeCustom
                                  ? "bg-primary text-white"
                                  : "bg-lightblue text-primary"
                              }`}
                              onClick={() => {
                                if (getDayWiseApiLoading) {
                                  showWarningToast(
                                    createJobValidations.isFetchingRates,
                                    {
                                      id: "isFetchingRates",
                                    },
                                  );
                                  return;
                                }
                                if (jobData.adminTime == item.value) {
                                  dispatch(
                                    updateJobData({
                                      adminTime: "00:00",
                                      isAdminTimeCustom: false,
                                    }),
                                  );
                                } else {
                                  dispatch(
                                    updateJobData({
                                      adminTime: item.value,
                                      isAdminTimeCustom: false,
                                    }),
                                  );
                                }
                                setAdminTimeLabelClicked(
                                  !adminTimeLabelClicked,
                                );
                              }}
                            >
                              {item.label}
                            </div>
                          ))}
                          <CustomField
                            isCustom={jobData.isAdminTimeCustom}
                            handleBlur={(val, customVal = true) => {
                              if (val !== jobData.adminTime) {
                                dispatch(
                                  updateJobData({
                                    adminTime: val,
                                    isAdminTimeCustom: customVal,
                                  }),
                                );
                              }
                            }}
                            curTime={jobData.adminTime}
                            labelClicked={adminTimeLabelClicked}
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-4">
                        <button
                          className="ease relative h-[40px] w-[120px] rounded-lg border border-primary bg-white text-[14px] font-medium leading-[38px] text-primary transition-all duration-200 hover:bg-none hover:bg-primary hover:text-white"
                          onClick={() => {
                            dispatch(updateActiveStep(2));
                          }}
                          type="button"
                        >
                          <span>Previous</span>
                        </button>
                        <button
                          className="ease relative h-[40px] w-[120px] rounded-lg border border-primary bg-primary text-[14px] font-medium leading-[38px] text-white transition-all duration-200 hover:bg-none hover:text-primary"
                          onClick={submitForm}
                          type="button"
                          disabled={isSubmitting || !isValid}
                        >
                          {isSubmitting ? <ButtonLoader /> : <span>Next</span>}
                        </button>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
          <Summary />
        </div>
        {jobData.totalDays > 0 && jobShiftObj.length ? <JobShiftLists /> : null}
      </div>
    </div>
  );
};
